import { Fragment, useState, useEffect } from 'react';
import { useAsync } from 'react-async';
import { getPendingCaseloads, approveCaseload, rejectCaseload } from '../../api/services/caseloadService';
import Layout from '../../components/layout';
import { Notification } from '../../components/notifications';
import LayoutError from '../../components/layoutError';
import moment from 'moment';
import DataTable from '../../components/dataTable';
import React from 'react';
import CaseloadDisplay from '../../api/models/caseloadDisplay';
import EditModal from '../../components/editModal';
import { useHistory, useLocation } from 'react-router-dom';
import CaseloadApprovalRequest from '../../api/requests/caseloadApprovalRequest';

type ViewState = {
  message?: string;
};

const ApproveCaseload = () => {
  const { state } = useLocation<ViewState>();
  const history = useHistory();
  const [caseloadsData, setCaseloadsData] = useState<CaseloadDisplay[]>([]);
  const [message, setMessage] = useState<string>(state?.message ?? '');
  const [error, setError] = useState<string>('');
  const [openRejectConfirmModal, setOpenRejectConfirmModal] = useState<boolean>(false);
  const [caseloadId, setCaseloadId] = useState<number>(0);
  const [notes, setNotes] = useState<string>('');

  const getCaseloads = useAsync({
    deferFn: getPendingCaseloads,
    onResolve: (r) => {
      setCaseloadsData(r.data.caseloads);
    }
  });

  useEffect(() => getCaseloads.run(), []);

  const columns = React.useMemo(
    () => [
      {
        Header: "Student Id",
        accessor: (s: any) => s.studentId,
      },
      {
        Header: 'Student Name',
        accessor: (s: any) => s.studentLastName + ', ' + s.studentFirstName
      },
      {
        Header: 'Discipline',
        accessor: (s: any) => s.disciplines ?? s.disciplineName
      },
      {
        Header: 'District',
        accessor: (s: any) => s.district ?? s.districtName,
        width: 250,
      },
      {
        Header: 'Program',
        accessor: (s: any) => s.program ?? s.programName,
        width: 250,
      },
      {
        Header: 'School',
        accessor: (s: any) => s.school ?? s.schoolName,
      },
      {
        Header: 'DOB',
        accessor: (s: any) => moment(s.dob).format('MM/DD/YYYY'),
      },
      {
        Header: 'Grade',
        accessor: (s: any) => s.grade ?? s.gradeName,
      },
      {
        Header: 'Requesting Therapist',
        accessor: (s: any) => `${s.therapistLastName}, ${s.therapistFirstName}` ?? s.therapistName
      },
      {
        Header: 'Actions',
        accessor: (s: any) => (
          <>
            <button
              className='mr-3 primary'
              onClick={(e) => {
                doApprove.run(s.caseloadId);
                e.stopPropagation();
              }}
            >
              Approve
            </button>
            <button
              className="bg-red-600 hover:bg-red-700"
              onClick={(e) => {
                setCaseloadId(s.caseloadId);
                setNotes(s.notes);
                setOpenRejectConfirmModal(true);
                e.stopPropagation();
              }}
            >
              Reject
            </button>
          </>
        ),
        width: 250,
        disableSortBy: true,
        sortable: false,

      },
      {
        accessor: 'studentId',
        className: 'hidden',
      },
      {
        accessor: 'caseloadId',
        className: 'hidden',
      },
      {
        accessor: 'therapistFirstName',
        className: 'hidden',
      },
      {
        accessor: 'therapistLastName',
        className: 'hidden',
      },
      {
        accessor: 'therapistName',
        className: 'hidden',
      },
    ],
    //Disabling because we do not need the dependency array
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  //Approval code
  const sendApprove = async (args: any) => {
    const caseloadId = args[0];
    return await approveCaseload(caseloadId);
  }
  const doApprove = useAsync({
    deferFn: sendApprove,
    onResolve: (data: any) => {
      getCaseloads.run();

      //Set the notification message
      setMessage(`Caseload approved successfully!`);
      setError('');
    },
    onReject: (data) => {
      setMessage('');
      setError('Error approving caseload');
    }
  });

  //Rejection code
  const sendReject = async (args: any) => {
    const caseloadId = args[0];
    const request: CaseloadApprovalRequest = {
      caseloadId,
      notes
    }
    return await rejectCaseload(caseloadId, request);
  }
  const doReject = useAsync({
    deferFn: sendReject,
    onResolve: (data: any) => {
      //Set the notification message
      setNotes('');
      setOpenRejectConfirmModal(false);
      setMessage(`Caseload rejected successfully!`);
      setError('');

      getCaseloads.run();
    },
    onReject: (error) => {
      setNotes('');
      setOpenRejectConfirmModal(false);

      setMessage('');
      setError('Error rejecting caseload');
    }
  });

  if (error) return <LayoutError />;
  return (
    <Layout isLoading={getCaseloads.isPending || doApprove.isPending || doReject.isPending}>
      <Fragment>
        {message !== '' && <Notification>{message}</Notification>}
        {error !== '' && <Notification>{error}</Notification>}
        <EditModal
          open={openRejectConfirmModal}
          saveText='Confirm'
          onSave={() => doReject.run(caseloadId)}
          onCancel={() => setOpenRejectConfirmModal(false)}
          id={caseloadId}
        >
          <h3>Are you sure you would like to reject this caseload?</h3>
          <h3>Notes</h3>
          <textarea value={notes ?? ''} onChange={(e) => setNotes(e.target.value)}></textarea>
          {/*<p><strong>Note:</strong> This will delete the student from the database</p>*/}
        </EditModal>
        <div className='grid grid-cols-5 gap-5 mb-6'>
          <div className='col-span-5 sm:col-span-5'>
            <div className='overflow-hidden border-b border-gray-200 rounded-lg caseloadsApprove datatable-editable'>
              {caseloadsData.length > 0 && (
                <DataTable
                  columns={columns}
                  data={caseloadsData}
                  showPagination={true}
                  getRowProps={(row: any) => ({
                    className: "cursor-pointer hover:bg-gray-100",
                    onClick: () => history.push(`/caseload/${row.values.caseloadId}`, {
                      referrer: `approve`,
                      therapistFirstName: row.values.therapistFirstName,
                      therapistLastName: row.values.therapistLastName
                    }),
                  })}
                />
              )}
              {(caseloadsData.length === 0 || caseloadsData === undefined) && (
                <h3>No pending caseloads were found</h3>
              )}
            </div>
          </div>
        </div>
      </Fragment>
    </Layout>
  );
};

export default ApproveCaseload;